import { zodResolver } from '@hookform/resolvers/zod';
import { Button, IconButton, TextField } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { onboardingAdmin } from 'further-ui/validations';
import { Controller, useForm } from 'react-hook-form';
import useInvestorUsers from './useInvestorUsers';
import { z } from 'zod';
import { Close } from '@mui/icons-material';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';

type FormValues = z.infer<typeof onboardingAdmin.AddInvestorUserInput>;
type Props = {
  vehicleApplicationId: string;
  vehicleId: string;
  showCloseButton: boolean;
  onClose: () => void;
};

const NewUserForm: React.FC<Props> = ({
  vehicleId,
  vehicleApplicationId,
  showCloseButton,
  onClose,
}) => {
  const { queryKey } = useVehicleApplication(vehicleId, vehicleApplicationId);
  const { handleAddInvestorUser, isLoading } = useInvestorUsers({
    vehicleApplicationQueryKey: queryKey,
  });

  const { handleSubmit, control } = useForm<FormValues>({
    resolver: zodResolver(onboardingAdmin.AddInvestorUserInput),
    defaultValues: {
      vehicleApplicationId,
      firstName: '',
      lastName: '',
      email: '',
    },
  });

  const onSubmit = (data: FormValues) => {
    if (isLoading) {
      return;
    }

    handleAddInvestorUser(
      {
        ...data,
        vehicleId,
        vehicleApplicationId,
      },
      {
        onSuccess: onClose,
      },
    );
  };

  return (
    <Column fullWidth spacing="lg">
      <Row fullWidth spacing="lg">
        <FormField label="First name">
          <Controller
            name="firstName"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="First name"
                error={!!error}
                helperText={error?.message}
                autoComplete="off"
              />
            )}
          />
        </FormField>
        <FormField label="Last name">
          <Controller
            name="lastName"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Last name"
                error={!!error}
                helperText={error?.message}
                autoComplete="off"
              />
            )}
          />
        </FormField>
        <FormField label="Email address">
          <Controller
            name="email"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Email address"
                error={!!error}
                helperText={error?.message}
                autoComplete="off"
              />
            )}
          />
        </FormField>
      </Row>
      <Row fullWidth justify="flex-end" spacing="sm" centered>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          Add user
        </Button>
        {showCloseButton ? (
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        ) : null}
      </Row>
    </Column>
  );
};

export default NewUserForm;
